import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const BG = '#0F1419';
const PRIMARY = '#4ECDC4';
const SECONDARY = '#FF6B6B';
const ACCENT = '#FFE66D';

const phaseOpacity = (frame: number, start: number, end: number, fade = 18) => {
  const fadeIn = interpolate(frame, [start, start + fade], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const fadeOut = interpolate(frame, [end - fade, end], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return fadeIn * fadeOut;
};

const GridAxes: React.FC<{ frame: number; emphasis?: boolean }> = ({ frame, emphasis }) => {
  const axisId = React.useId().replace(/:/g, '');
  const tick = 52;
  const size = 520;
  const origin = size / 2;
  const lines: React.ReactNode[] = [];
  for (let i = -5; i <= 5; i++) {
    const pos = origin + i * tick;
    const pulse = emphasis ? 0.08 + Math.sin(frame * 0.1 + i) * 0.05 : 0.06;
    lines.push(
      <line
        key={`v-${i}`}
        x1={pos}
        y1={40}
        x2={pos}
        y2={size - 40}
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={i === 0 ? 2.5 : 1}
        opacity={i === 0 ? 0.9 : pulse * 10}
      />,
    );
    lines.push(
      <line
        key={`h-${i}`}
        x1={40}
        y1={pos}
        x2={size - 40}
        y2={pos}
        stroke="rgba(255,255,255,0.12)"
        strokeWidth={i === 0 ? 2.5 : 1}
        opacity={i === 0 ? 0.9 : pulse * 10}
      />,
    );
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <marker id={axisId} markerWidth="10" markerHeight="10" refX="5" refY="5" orient="auto">
          <polygon points="0 0, 10 5, 0 10" fill={PRIMARY} />
        </marker>
      </defs>
      {lines}
      <line
        x1={40}
        y1={origin}
        x2={size - 30}
        y2={origin}
        stroke={PRIMARY}
        strokeWidth={2.5}
        markerEnd={`url(#${axisId})`}
      />
      <line
        x1={origin}
        y1={size - 40}
        x2={origin}
        y2={50}
        stroke={SECONDARY}
        strokeWidth={2.5}
        markerEnd={`url(#${axisId})`}
      />
      <text x={size - 28} y={origin + 28} fill={PRIMARY} fontSize={20} fontWeight={700}>
        x
      </text>
      <text x={origin + 14} y={58} fill={SECONDARY} fontSize={20} fontWeight={700}>
        y
      </text>
    </svg>
  );
};

const VectorArrow: React.FC<{
  ox: number;
  oy: number;
  vx: number;
  vy: number;
  color: string;
  label: string;
  opacity: number;
  frame: number;
}> = ({ ox, oy, vx, vy, color, label, opacity, frame }) => {
  const len = Math.sqrt(vx * vx + vy * vy) || 1;
  const ux = vx / len;
  const uy = vy / len;
  const shorten = 22;
  const x2 = ox + vx - ux * shorten;
  const y2 = oy - vy + uy * shorten;
  const wobble = Math.sin(frame * 0.09) * 2;
  return (
    <g opacity={opacity}>
      <defs>
        <marker id={`va-${label}`} markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto">
          <polygon points="0 0, 12 6, 0 12" fill={color} />
        </marker>
      </defs>
      <line
        x1={ox}
        y1={oy + wobble}
        x2={x2}
        y2={y2 + wobble}
        stroke={color}
        strokeWidth={5}
        strokeLinecap="round"
        markerEnd={`url(#va-${label})`}
      />
      <text
        x={ox + vx * 0.55 + 16}
        y={oy - vy * 0.55 - 10 + wobble}
        fill={color}
        fontSize={24}
        fontWeight={700}
        fontFamily="system-ui, sans-serif"
      >
        {label}
      </text>
    </g>
  );
};

const MatrixBox: React.FC<{
  rows: number[][];
  highlight?: [number, number] | null;
  fillProgress: number;
  label: string;
}> = ({ rows, highlight, fillProgress, label }) => {
  const cell = 68;
  const pad = 16;
  const w = rows[0].length * cell + pad * 2;
  const h = rows.length * cell + pad * 2 + 36;
  let order = 0;
  const total = rows.length * rows[0].length;
  return (
    <svg width={w} height={h}>
      <text x={pad} y={26} fill={ACCENT} fontSize={22} fontWeight={700} fontFamily="sans-serif">
        {label}
      </text>
      <rect
        x={4}
        y={34}
        width={w - 8}
        height={h - 38}
        rx={12}
        fill="rgba(30,41,59,0.6)"
        stroke={PRIMARY}
        strokeWidth={2}
      />
      {rows.map((row, ri) =>
        row.map((val, ci) => {
          const idx = order++;
          const show = interpolate(fillProgress, [idx / total, (idx + 1) / total], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          const isHi = highlight && highlight[0] === ri && highlight[1] === ci;
          return (
            <g key={`${ri}-${ci}`}>
              <rect
                x={pad + ci * cell}
                y={40 + pad + ri * cell}
                width={cell - 6}
                height={cell - 6}
                rx={10}
                fill={isHi ? `${SECONDARY}44` : 'rgba(15,23,42,0.9)'}
                stroke={isHi ? SECONDARY : `${PRIMARY}55`}
                strokeWidth={isHi ? 3 : 1}
                opacity={show}
              />
              <text
                x={pad + ci * cell + (cell - 6) / 2}
                y={40 + pad + ri * cell + (cell - 6) / 2 + 9}
                textAnchor="middle"
                fill="#fff"
                fontSize={28}
                fontWeight={700}
                fontFamily="monospace"
                opacity={show}
              >
                {val}
              </text>
            </g>
          );
        }),
      )}
    </svg>
  );
};

const MatrixMultiplyVisual: React.FC<{ frame: number; phaseStart: number }> = ({ frame, phaseStart }) => {
  const t = Math.max(0, frame - phaseStart);
  const step = Math.min(3, Math.floor(t / 35));
  const A = [
    [2, 1],
    [0, 3],
  ];
  const B = [
    [1, 4],
    [2, 0],
  ];
  const C = [
    [4, 8],
    [6, 0],
  ];
  const fillA = interpolate(t, [0, 40], [0, 1], { extrapolateRight: 'clamp' });
  const fillB = interpolate(t, [25, 65], [0, 1], { extrapolateRight: 'clamp' });
  const fillC = interpolate(t, [110, 200], [0, 1], { extrapolateRight: 'clamp' });
  const hi: [number, number] | null =
    step === 0 ? [0, 0] : step === 1 ? [0, 1] : step === 2 ? [1, 0] : [1, 1];
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 32, flexWrap: 'wrap' }}>
      <MatrixBox rows={A} highlight={step < 2 ? hi : null} fillProgress={fillA} label="A" />
      <span style={{ fontSize: 54, color: ACCENT, paddingBottom: 50, fontWeight: 300 }}>×</span>
      <MatrixBox rows={B} highlight={step < 2 ? hi : null} fillProgress={fillB} label="B" />
      <span style={{ fontSize: 54, color: ACCENT, paddingBottom: 50, fontWeight: 300 }}>=</span>
      <MatrixBox rows={C} highlight={null} fillProgress={fillC} label="C = AB" />
    </div>
  );
};

const LinearTransformDemo: React.FC<{ frame: number }> = ({ frame }) => {
  const ox = 260;
  const oy = 260;
  const angle = interpolate(frame % 150, [0, 150], [0, Math.PI / 3]);
  const scale = 1 + Math.sin(frame * 0.05) * 0.15;
  const cos = Math.cos(angle) * scale;
  const sin = Math.sin(angle) * scale;
  const apply = (x: number, y: number) => ({
    x: ox + cos * x - sin * y,
    y: oy - (sin * x + cos * y),
  });
  const e1 = apply(110, 0);
  const e2 = apply(0, 110);
  const v = apply(75, 85);
  return (
    <div style={{ position: 'relative', width: 520, height: 520 }}>
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
        <GridAxes frame={frame} emphasis />
      </div>
      <svg
        width={520}
        height={520}
        viewBox="0 0 520 520"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      >
        <polygon
          points={`${ox},${oy} ${ox + 110},${oy} ${ox + 110},${oy - 110} ${ox},${oy - 110}`}
          fill="rgba(78,205,196,0.12)"
          stroke={PRIMARY}
          strokeWidth={2}
          strokeDasharray="6 6"
          opacity={0.45}
        />
        <line x1={ox} y1={oy} x2={e1.x} y2={e1.y} stroke={PRIMARY} strokeWidth={4} />
        <line x1={ox} y1={oy} x2={e2.x} y2={e2.y} stroke={SECONDARY} strokeWidth={4} />
        <line x1={ox} y1={oy} x2={v.x} y2={v.y} stroke={ACCENT} strokeWidth={5} />
        <text x={e1.x + 10} y={e1.y} fill={PRIMARY} fontSize={20} fontWeight={700}>
          Aê₁
        </text>
        <text x={e2.x - 4} y={e2.y - 12} fill={SECONDARY} fontSize={20} fontWeight={700}>
          Aê₂
        </text>
        <text x={v.x + 8} y={v.y - 8} fill={ACCENT} fontSize={20} fontWeight={700}>
          Av
        </text>
      </svg>
    </div>
  );
};

const EigenSketch: React.FC<{ frame: number }> = ({ frame }) => {
  const ox = 280;
  const oy = 280;
  const lambda = 1.4 + Math.sin(frame * 0.06) * 0.25;
  const vx = 130;
  const vy = -60;
  const ex = ox + vx * lambda;
  const ey = oy + vy * lambda;
  const pulse = spring({
    frame: frame % 90,
    fps: 30,
    config: { damping: 12, stiffness: 80 },
  });
  const glow = interpolate(pulse, [0, 1], [0.4, 1]);
  return (
    <svg width={560} height={400} viewBox="0 0 560 400">
      <line
        x1={60}
        y1={oy}
        x2={520}
        y2={oy}
        stroke="rgba(255,255,255,0.15)"
        strokeWidth={1}
      />
      <line
        x1={ox}
        y1={360}
        x2={ox}
        y2={40}
        stroke="rgba(255,255,255,0.15)"
        strokeWidth={1}
      />
      <line
        x1={ox}
        y1={oy}
        x2={ox + vx}
        y2={oy + vy}
        stroke={PRIMARY}
        strokeWidth={4}
        opacity={0.85}
      />
      <line
        x1={ox}
        y1={oy}
        x2={ex}
        y2={ey}
        stroke={ACCENT}
        strokeWidth={6}
        opacity={glow}
        strokeLinecap="round"
      />
      <text x={ox + vx / 2 - 30} y={oy + vy / 2 - 14} fill={PRIMARY} fontSize={22} fontWeight={700}>
        v
      </text>
      <text x={(ox + ex) / 2 + 12} y={(oy + ey) / 2} fill={ACCENT} fontSize={22} fontWeight={700}>
        Av = λv
      </text>
      <text x={72} y={52} fill="rgba(255,255,255,0.55)" fontSize={22} fontFamily="sans-serif">
        特征向量方向不变，仅被拉伸 λ 倍
      </text>
    </svg>
  );
};

export const LinearAlgebraAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleSpring = spring({ frame, fps, config: { damping: 15, stiffness: 95 } });
  const titleY = interpolate(titleSpring, [0, 1], [-44, 0]);

  const pTitle = phaseOpacity(frame, 0, 100, 20);
  const pVec = phaseOpacity(frame, 80, 210, 22);
  const pTransform = phaseOpacity(frame, 190, 320, 22);
  const pMult = phaseOpacity(frame, 300, 450, 22);
  const pEigen = phaseOpacity(frame, 430, 540, 20);

  const origin = 260;
  const vecReveal = interpolate(frame, [95, 160], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse 100% 70% at 75% 15%, ${SECONDARY}18 0%, transparent 55%),
          radial-gradient(ellipse 80% 60% at 10% 80%, ${PRIMARY}16 0%, transparent 50%),
          linear-gradient(165deg, #08090c 0%, ${BG} 50%, #0B1120 100%)`,
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.25,
          backgroundImage: `radial-gradient(circle at 20% 30%, ${PRIMARY} 0%, transparent 2px),
            radial-gradient(circle at 80% 70%, ${SECONDARY} 0%, transparent 2px)`,
          backgroundSize: '120px 120px',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'absolute',
          top: 44,
          left: 80,
          opacity: pTitle * titleSpring,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 92,
            fontWeight: 900,
            background: `linear-gradient(100deg, ${PRIMARY}, ${ACCENT}, ${SECONDARY})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          线性代数
        </h1>
        <p style={{ margin: '16px 0 0', fontSize: 36, color: 'rgba(255,255,255,0.48)' }}>
          矩阵运算与向量空间
        </p>
      </div>

      <div
        style={{
          position: 'absolute',
          top: 140,
          left: 80,
          right: 80,
          opacity: pVec,
          display: 'flex',
          gap: 40,
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, maxWidth: 580 }}>
          <h2 style={{ margin: 0, fontSize: 44, color: PRIMARY, fontWeight: 800 }}>向量与坐标</h2>
          <p style={{ fontSize: 28, lineHeight: 1.65, color: 'rgba(255,255,255,0.55)', marginTop: 16 }}>
            平面上的箭头可用分量表示；加法满足平行四边形法则。整个平面构成 ℝ² 向量空间的一个模型。
          </p>
          <div
            style={{
              marginTop: 24,
              padding: '22px 28px',
              borderRadius: 16,
              background: 'rgba(30,41,59,0.55)',
              border: `1px solid ${PRIMARY}44`,
              fontSize: 26,
              color: 'rgba(255,255,255,0.7)',
              fontFamily: 'monospace',
            }}
          >
            u = (u₁, u₂), v = (v₁, v₂) → u + v = (u₁+v₁, u₂+v₂)
          </div>
        </div>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <GridAxes frame={frame} />
          <div style={{ position: 'absolute', left: 0, top: 0, width: 520, height: 520 }}>
            <svg width={520} height={520} viewBox="0 0 520 520">
              <VectorArrow
                ox={origin}
                oy={origin}
                vx={155}
                vy={-90}
                color={PRIMARY}
                label="u"
                opacity={vecReveal}
                frame={frame}
              />
              <VectorArrow
                ox={origin}
                oy={origin}
                vx={-70}
                vy={-130}
                color={SECONDARY}
                label="v"
                opacity={interpolate(vecReveal, [0, 1], [0, 1]) * 0.95}
                frame={frame}
              />
              <VectorArrow
                ox={origin}
                oy={origin}
                vx={85}
                vy={-220}
                color={ACCENT}
                label="u+v"
                opacity={interpolate(frame, [130, 185], [0, 1], {
                  extrapolateLeft: 'clamp',
                  extrapolateRight: 'clamp',
                })}
                frame={frame}
              />
            </svg>
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          top: 100,
          left: 80,
          right: 80,
          opacity: pTransform,
          display: 'flex',
          gap: 40,
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, maxWidth: 580 }}>
          <h2 style={{ margin: '0 0 20px', fontSize: 46, color: SECONDARY, fontWeight: 800 }}>
            矩阵即线性变换
          </h2>
          <p style={{ fontSize: 28, color: 'rgba(255,255,255,0.52)', lineHeight: 1.65 }}>
            矩阵 A 把基向量 ê₁、ê₂ 映射到新方向；正方形网格随之变为平行四边形，面积缩放与行列式相关。
          </p>
        </div>
        <LinearTransformDemo frame={frame} />
      </div>

      <div
        style={{
          position: 'absolute',
          top: 100,
          left: 80,
          opacity: pMult,
        }}
      >
        <h2 style={{ margin: '0 0 24px', fontSize: 46, color: ACCENT, fontWeight: 800 }}>
          矩阵乘法：行 × 列
        </h2>
        <p style={{ fontSize: 28, color: 'rgba(255,255,255,0.5)', marginBottom: 24, maxWidth: 1100 }}>
          结果矩阵第 (i,j) 元为 A 的第 i 行与 B 的第 j 列对应乘积之和，对应线性变换的复合。
        </p>
        <MatrixMultiplyVisual frame={frame} phaseStart={310} />
      </div>

      <div
        style={{
          position: 'absolute',
          top: 100,
          left: 80,
          right: 80,
          opacity: pEigen,
          display: 'flex',
          gap: 40,
          alignItems: 'center',
        }}
      >
        <div style={{ flex: 1, maxWidth: 580 }}>
          <h2 style={{ margin: '0 0 18px', fontSize: 48, color: PRIMARY, fontWeight: 800 }}>
            特征值与特征向量
          </h2>
          <p style={{ fontSize: 28, color: 'rgba(255,255,255,0.55)', lineHeight: 1.65 }}>
            若 Av = λv 且 v ≠ 0，则 v 为特征向量，λ 为特征值。方向在变换下保持不变，模长按 λ 缩放。
          </p>
          <ul style={{ color: 'rgba(255,255,255,0.45)', fontSize: 25, lineHeight: 1.8, paddingLeft: 24 }}>
            <li>求解 det(A − λI) = 0 得特征值</li>
            <li>主成分分析、振动模态、PageRank 等均依赖谱思想</li>
          </ul>
        </div>
        <EigenSketch frame={frame} />
      </div>

      <div
        style={{
          position: 'absolute',
          bottom: 24,
          right: 44,
          fontSize: 24,
          color: 'rgba(255,255,255,0.22)',
        }}
      >
        线性代数动画 · 示意教学
      </div>
    </AbsoluteFill>
  );
};
